import "../styles/reports.css";
import RangeSummaryCard from "../components/RangeSummaryCard";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { getHistory } from "../services/history";
import { socket } from "../services/socket";
import { getActiveSerial } from "../services/activeDevice";
import { getRealtimeHistory } from "../services/stats";
import { useEffect, useMemo, useState } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from "chart.js";

import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

// Consistent color per quantity across every chart, so "PV" is
// always the same green whether you're looking at power, voltage,
// current, or the historical chart.
const COLOR = {
    pv: "#2ED573",
    grid: "#FF8C00",
    load: "#3FA9F5",
    temp: "#FF4757"
};

// ---------------------------------------------------------
// Postgres timestamps often come back as "YYYY-MM-DD HH:MM:SS"
// (a space, no "T", no timezone) - `new Date(...)` parses that
// unreliably across browsers, which is what actually produces
// "Invalid Date" rather than the field being missing outright.
// Normalize the string before parsing, and try a short list of
// likely field names rather than assuming one exact name.
// ---------------------------------------------------------
const TIMESTAMP_KEYS = [
    "label","created_at", "timestamp", "time", "recorded_at", "logged_at", "ts", "updated_at"
];

let loggedMissingTimestampKey = false;

function parseTimestamp(value) {

    if (value === null || value === undefined) return null;
    if (value instanceof Date) return value;

    let str = String(value);

    // PostgreSQL DATE values are returned as YYYY-MM-DD.
    // Treat them as local dates instead of UTC dates.
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        str += "T00:00:00";
    }

    // PostgreSQL timestamp without timezone
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(str)) {
        str = str.replace(" ", "T");
    }

    const d = new Date(str);

    return isNaN(d.getTime()) ? null : d;

}

function toTimeLabel(item) {

    for (const key of TIMESTAMP_KEYS) {

        const parsed = parseTimestamp(item[key]);

        if (parsed) {
            return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        }

    }

    if (!loggedMissingTimestampKey) {

        loggedMissingTimestampKey = true;

        console.warn(
            "Reports: couldn't find a parseable timestamp field on this row - " +
            "checked", TIMESTAMP_KEYS, "actual keys were:", Object.keys(item)
        );

    }

    return "--:--";

}

// One shared options-builder instead of a near-identical object
// repeated per chart - the only thing that actually changes
// between charts is the unit on the Y axis.
function buildOptions(unit) {

    return {

        responsive: true,
        maintainAspectRatio: false,

        elements: {
            line: {
                borderCapStyle: "round",
                borderJoinStyle: "round"
            }
        },

        interaction: {
            mode: "index",
            intersect: false
        },

        plugins: {
            legend: {
                labels: {
                    color: "#cfd8e6",
                    boxWidth: 12,
                    boxHeight: 12,
                    usePointStyle: true
                }
            },
            tooltip: {
                callbacks: {
                    label: (ctx) =>
                        `${ctx.dataset.label}: ${ctx.formattedValue} ${unit}`
                }
            }
        },

        scales: {

            x: {
                ticks: {
                    color: "#9FB2C7",
                    autoSkip: true,
                    maxRotation: 0,
                    maxTicksLimit: 8
                },
                grid: { color: "#263445" }
            },

            y: {
                beginAtZero: true,
                ticks: {
                    color: "#9FB2C7",
                    callback: (value) => `${value} ${unit}`
                },
                grid: { color: "#263445" }
            }

        }

    };

}

function lineSet(label, data, color) {

    return {
        label,
        data,
        borderColor: color,
        backgroundColor: color,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3
    };

}

// A graph card that handles its own empty state instead of
// silently rendering a blank box when there's no data yet.
function GraphCard({ title, data, options, empty }) {

    return (

        <div className="graphCard">

            <h3>{title}</h3>

            {empty ? (

                <div className="graphEmpty">No data for this period yet.</div>

            ) : (

                <div className="graphCanvas">
                    <Line data={data} options={options} />
                </div>

            )}

        </div>

    );

}

export default function Reports() {

    const [menuOpen, setMenuOpen] = useState(false);
    const [graphData, setGraphData] = useState([]);
    const [activeSerial, setActiveSerial] = useState(getActiveSerial());
    const [deviceStatus, setDeviceStatus] = useState("Offline");
    const [range, setRange] = useState("today");
    const [historyData, setHistoryData] = useState([]);

    // ---------------- Live readings (auto-refreshes every 60s) ----------------
    useEffect(() => {

        async function loadGraph() {

            try {

                const res = await getRealtimeHistory(activeSerial);
                setGraphData(Array.isArray(res.data) ? res.data : []);

            } catch (err) {

                console.error(err);
                setGraphData([]);

            }

        }

        loadGraph();

        const timer = setInterval(loadGraph, 60000);

        return () => clearInterval(timer);

    }, [activeSerial]);

    // ---------------- Live online/offline status ----------------
    useEffect(() => {

        if (!activeSerial) return;

        function onStatus(msg) {
            if (msg.serial === activeSerial) setDeviceStatus(msg.status);
        }

        socket.on("device:status", onStatus);

        return () => socket.off("device:status", onStatus);

    }, [activeSerial]);

    // ---------------- React to switching the active device elsewhere in the app ----------------
    useEffect(() => {

        function handleDeviceChange() {
            setActiveSerial(getActiveSerial());
        }

        window.addEventListener("active-device-changed", handleDeviceChange);

        return () => window.removeEventListener("active-device-changed", handleDeviceChange);

    }, []);

    // ---------------- Historical range data ----------------
    useEffect(() => {

        async function loadHistory() {

            try {

                const res = await getHistory(activeSerial, range);
                setHistoryData(Array.isArray(res.data) ? res.data : []);

            } catch (err) {

                console.error(err);
                setHistoryData([]);

            }

        }

        if (activeSerial) loadHistory();

    }, [activeSerial, range]);

    // ---------------- Chart datasets ----------------
    // Split by unit rather than one chart with Watts, Volts, Amps
    // and °C all sharing a single axis - on one scale, anything
    // that isn't in the thousands (V, A, °C) flattens to a line
    // along the bottom and becomes unreadable.
    const labels = useMemo(() => (

        graphData.map(toTimeLabel)

    ), [graphData]);

    const powerData = {
        labels,
        datasets: [
            lineSet("PV Power", graphData.map(r => r.pv_w), COLOR.pv),
            lineSet("Grid Power", graphData.map(r => r.grid_w), COLOR.grid),
            lineSet("Load Power", graphData.map(r => r.out_ap), COLOR.load)
        ]
    };

    const voltageData = {
        labels,
        datasets: [
            lineSet("PV Voltage", graphData.map(r => r.pv_v), COLOR.pv),
            lineSet("Grid Voltage", graphData.map(r => r.grid_v), COLOR.grid),
            lineSet("Load Voltage", graphData.map(r => r.out_v), COLOR.load)
        ]
    };

    const currentData = {
        labels,
        datasets: [
            lineSet("PV Current", graphData.map(r => r.pv_i), COLOR.pv),
            lineSet("Grid Current", graphData.map(r => r.grid_i), COLOR.grid),
            lineSet("Load Current", graphData.map(r => r.out_i), COLOR.load)
        ]
    };

    const temperatureData = {
        labels,
        datasets: [
            lineSet("Temperature", graphData.map(r => r.hs_t), COLOR.temp)
        ]
    };

    // Match the label's granularity to what was actually asked for -
    // showing "Jan 14, 10:32" for a whole year of data is as unreadable
    // as showing bare minutes for a single day.
    const historicalLabels = historyData.map(r => {

        const d = parseTimestamp(r.label) || new Date(r.label);

        if (range === "today" || range === "yesterday") {

            return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

        }

        if (range === "year") {

            return d.toLocaleDateString([], { month: "short", year: "numeric" });

        }

        // week / month / lastmonth
        return d.toLocaleDateString([], { month: "short", day: "numeric" });

    });

    // "year" returns monthly ENERGY totals (kWh), not power readings
    // (W) like every other range does - these are genuinely different
    // quantities, so this chart needs a different title/unit/field
    // mapping for that one case rather than pretending it's the same
    // "PV Power vs Load Power" data.
    const isYearView = range === "year";

    const historicalData = {
        labels: historicalLabels,
        datasets: isYearView
            ? [
                lineSet("PV Energy", historyData.map(r => Number(r.solar_energy_kwh)), COLOR.pv),
                lineSet("Grid Energy", historyData.map(r => Number(r.grid_energy_kwh)), COLOR.grid)
            ]
            : [
                lineSet("PV Power", historyData.map(r => Number(r.pv_w)), COLOR.pv),
                lineSet("Load Power", historyData.map(r => Number(r.out_ap)), COLOR.load)
            ]
    };

    const historicalOptions = useMemo(
        () => buildOptions(isYearView ? "kWh" : "W"),
        [isYearView]
    );

    const powerOptions = useMemo(() => buildOptions("W"), []);
    const voltageOptions = useMemo(() => buildOptions("V"), []);
    const currentOptions = useMemo(() => buildOptions("A"), []);
    const tempOptions = useMemo(() => buildOptions("°C"), []);

    return (

        <div className="layout">

            <Sidebar
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
            />

            <div className="content">

                <Topbar
                    serial={activeSerial}
                    deviceStatus={deviceStatus}
                    setMenuOpen={setMenuOpen}
                />

                <div className="reportsBody">

                    <div className="reportsHeader">

                        <div>
                            <h2>Reports</h2>
                            <p>Live readings and historical trends for this device</p>
                        </div>

                        <select
                            value={range}
                            onChange={(e) => setRange(e.target.value)}
                        >
                            <option value="today">Today</option>
                            <option value="yesterday">Yesterday</option>
                            <option value="week">Last 7 Days</option>
                            <option value="month">This Month</option>
                            <option value="lastmonth">Last Month</option>
                            <option value="year">This Year</option>
                        </select>

                    </div>

                    <div className="reportsSectionLabel">
                        <span className="liveDotSmall" />
                        Live (last few hours, refreshes every minute)
                    </div>

                    <div className="graphGrid">

                        <GraphCard
                            title="Power"
                            data={powerData}
                            options={powerOptions}
                            empty={graphData.length === 0}
                        />

                        <GraphCard
                            title="Voltage"
                            data={voltageData}
                            options={voltageOptions}
                            empty={graphData.length === 0}
                        />

                        <GraphCard
                            title="Current"
                            data={currentData}
                            options={currentOptions}
                            empty={graphData.length === 0}
                        />

                        <GraphCard
                            title="Temperature"
                            data={temperatureData}
                            options={tempOptions}
                            empty={graphData.length === 0}
                        />

                    </div>

                    <div className="reportsSectionLabel">Historical Power</div>

                    <RangeSummaryCard
                        serial={activeSerial}
                        range={range}
                    />

                    <GraphCard
                        title={
                            isYearView
                                ? `PV vs Grid Energy - ${range}`
                                : `PV vs Load Power - ${range}`
                        }
                        data={historicalData}
                        options={historicalOptions}
                        empty={historyData.length === 0}
                    />

                </div>

            </div>

        </div>

    );

}
